<template>
    <nav class="navbar"
         :class="[
            {'navbar-expand-md': expand},
            {'navbar-transparent': transparent},
            {[`bg-${type}`]: type}
         ]">
        <div :class="containerClasses" class="navbar-main-container">
            <!-- Desktop brand (left side, hidden on mobile) -->
            <slot name="brand">
                <router-link :to="$route.path"
                             class="navbar-brand-desktop d-none d-lg-block">
                    {{$route.name}}
                </router-link>
            </slot>
            
            <!-- Single row: toggle, mobile brand, and menu items -->
            <div class="navbar-row-container">
                <!-- Toggle button -->
                <navbar-toggle-button v-if="showToggleButton"
                                      :toggled="toggled"
                                      :target="contentId"
                                      @click.native.stop="toggled = !toggled">
                    <span class="navbar-toggler-icon"></span>
                </navbar-toggle-button>
                
                <!-- Brand: Mobile version (shown only on mobile) -->
                <router-link :to="$route.path"
                             class="navbar-brand-mobile d-lg-none">
                    {{$route.name}}
                </router-link>

                <!-- Menu items (visible on both mobile and desktop) -->
                <div class="navbar-items-container">
                    <slot :close-menu="closeMenu"></slot>
                </div>
            </div>
        </div>
    </nav>
</template>
<script>
  import NavbarToggleButton from "./NavbarToggleButton";

  export default {
    name: "base-nav",
    components: {
      NavbarToggleButton
    },
    props: {
      type: {
        type: String,
        default: "",
        description: "Navbar type (e.g default, primary etc)"
      },
      title: {
        type: String,
        default: "",
        description: "Title of navbar"
      },
      contentId: {
        type: [String, Number],
        default: Math.random().toString(),
        description:
          "Explicit id for the menu. By default it's a generated random number"
      },
      containerClasses: {
        type: [String, Object, Array],
        default: 'container-fluid'
      },
      transparent: {
        type: Boolean,
        default: false,
        description: "Whether navbar is transparent"
      },
      expand: {
        type: Boolean,
        default: false,
        description: "Whether navbar should contain `navbar-expand-lg` class"
      },
      showToggleButton: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        toggled: false
      };
    },
    methods: {
      closeMenu() {
        this.toggled = false;
      }
    }
  };
</script>
<style scoped>
.navbar {
  display: flex !important;
  align-items: center !important;
  flex-wrap: nowrap !important;
  height: auto !important;
  min-height: 56px;
}

.navbar-main-container {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  width: 100%;
  flex-wrap: nowrap !important;
  height: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

.navbar-row-container {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  flex-wrap: nowrap !important;
  margin: 0 !important;
  padding: 0.5rem 0 !important;
}

.navbar-items-container {
  display: flex !important;
  align-items: center !important;
  margin-left: auto !important;
  flex-wrap: nowrap !important;
  height: 100%;
  flex-shrink: 1;
}

.navbar-items-container ::v-deep .navbar-nav {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-end !important;
  margin: 0 !important;
  padding: 0 !important;
  gap: 0.5rem;
  flex-wrap: nowrap !important;
  height: 100%;
}

.navbar-items-container ::v-deep .nav-item {
  margin: 0 !important;
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  height: 100%;
}

.navbar,
.navbar .collapse,
.navbar .navbar-collapse,
.navbar .navbar-nav,
.navbar .form-inline,
.navbar > div {
  background: var(--header-bg) !important;
  color: var(--header-text) !important;
}

.navbar .nav-link,
.navbar .dropdown-toggle,
.navbar .navbar-brand,
.navbar .navbar-text,
.navbar .router-link-active {
  color: var(--header-text) !important;
}

.navbar .navbar-toggler-icon {
  color: var(--header-text);
}

.navbar .form-control,
.navbar .input-group-text {
  background-color: rgba(255, 255, 255, 0.14) !important;
  color: var(--header-text) !important;
  border-color: rgba(255, 255, 255, 0.22) !important;
}

/* Desktop brand styling */
.navbar-brand-desktop {
  font-size: 1rem;
  font-weight: 600;
  color: var(--header-text) !important;
  text-decoration: none !important;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  margin: 0 !important;
  padding: 0 !important;
  align-items: center !important;
  height: 100%;
  display: inline-flex;
}

/* Mobile brand styling */
.navbar-brand-mobile {
  min-width: 0;
  max-width: min(14rem, calc(100vw - 6rem));
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: clamp(0.85rem, 3.5vw, 1.05rem);
  font-weight: 600;
  color: var(--header-text) !important;
  text-decoration: none !important;
  text-transform: uppercase;
  white-space: nowrap;
  flex: 1 1 auto;
  margin: 0 !important;
  padding: 0 0.25rem !important;
  align-items: center !important;
  height: 100%;
  display: inline-flex;
}

/* Ensure toggle button is visible and aligned */
.navbar-row-container ::v-deep .navbar-toggler {
  flex-shrink: 0 !important;
  margin: 0 !important;
  padding: 0.35rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100%;
}

/* Hide desktop profile dropdown on mobile */
@media (max-width: 991.98px) {
  .navbar ::v-deep ul.navbar-nav.d-lg-flex {
    display: none !important;
  }
}
</style>
